import { FC, useCallback, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Paper, Grid, Button, TextField, CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { fetchAllShifts } from 'core/API/shifts';
import {
	createSchedulerMetric,
	updateSchedulerMetric,
	getSchedulerMetrics,
	getGeneralSettings,
} from 'core/API/scheduler';
import {
	buildSchedulerMetricPayload,
	calculateDayHoursFromShifts,
	getMetricDay,
} from '../../utils/schedulerMetricsUtils';

import './EditStatsPage.scss';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const validationSchema = Yup.object().shape({
  metrics: Yup.array().of(
    Yup.object().shape({
      sales: Yup.number().min(0, 'Sales must be a positive number'),
      customers: Yup.number().min(0, 'Customers must be a positive number'),
    })
  ),
});

interface IDayMetric {
  date?: string;
  day: string;
  dayOfWeek: string;
  sales: number;
  customers: number;
  hoursScheduled: number;
  otHoursScheduled: number;
  metricId?: number;
}

interface IEditStatsForm {
  metrics: IDayMetric[];
}

interface IEditStatsPage {
  onBack: () => void;
  startDate?: string;
  endDate?: string;
}

const EditStatsPage: FC<IEditStatsPage> = ({ onBack, startDate, endDate }) => {
  const { showToast } = useToastContext();
  const { workplaceId } = useWorkplacesContext();
  const queryClient = useQueryClient();

  const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : undefined;
  const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : undefined;

  // Refetch metrics when page unmounts (user navigates away from EditStatsPage)
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries([
        'scheduler-metrics',
        workplaceId,
        formattedStartDate,
        formattedEndDate,
      ]);
    };
  }, [queryClient, workplaceId, formattedStartDate, formattedEndDate]);

  // Fetch shifts for the week to calculate hours scheduled (does not block budget entry)
  const { data: shiftsData } = useQuery(
    ['shifts-for-metrics', workplaceId, formattedStartDate, formattedEndDate],
    () => fetchAllShifts({
      workplaceId,
      fromDate: formattedStartDate || moment().format('YYYY-MM-DD'),
      toDate: formattedEndDate || moment().add(6, 'days').format('YYYY-MM-DD'),
    }),
    {
      enabled: !!workplaceId && !!formattedStartDate && !!formattedEndDate,
    }
  );

  // Fetch existing metrics
  const { data: existingMetrics, isLoading: isLoadingMetrics } = useQuery(
    ['scheduler-metrics', workplaceId, formattedStartDate, formattedEndDate],
    () => getSchedulerMetrics(workplaceId, formattedStartDate, formattedEndDate),
    {
      enabled: !!workplaceId && !!formattedStartDate && !!formattedEndDate,
    }
  );

  const { data: generalSettings } = useQuery('generalSettings', () => getGeneralSettings());

  // Create/Update metric mutations
  const createMetricMutation = useMutation((payload: any) => createSchedulerMetric(payload));
  const updateMetricMutation = useMutation(
    (variables: { id: number; payload: any }) => updateSchedulerMetric(variables.id, variables.payload)
  );

  const dailyOTThreshold = generalSettings?.overtimeDailyHoursAt || 8;

  // Calculate hours scheduled for a specific day from live shifts
  const calculateHoursForDay = useCallback((dayDate: string) => {
    return calculateDayHoursFromShifts(shiftsData?.member || [], dayDate, dailyOTThreshold);
  }, [shiftsData, dailyOTThreshold]);

  // Initialize form with 7 days starting from startDate
  const initializeMetrics = useCallback((): IDayMetric[] => {
    const metrics: IDayMetric[] = [];
    const startMoment = moment(startDate || moment().format('YYYY-MM-DD'));

    for (let i = 0; i < 7; i++) {
      const currentDate = startMoment.clone().add(i, 'days');
      const dateStr = currentDate.format('YYYY-MM-DD');
      const dayOfWeek = DAYS_OF_WEEK[currentDate.day() === 0 ? 6 : currentDate.day() - 1];

      // Find existing metric from API response
      const existingMetricData = existingMetrics?.find(
        (metric: any) => getMetricDay(metric) === dateStr
      ) as any;
      const hours = calculateHoursForDay(dateStr);

      // Use sales and customers directly from API if available, otherwise use 0
      const sales = existingMetricData?.sales || 0;
      const customers = existingMetricData?.customers || 0;

      metrics.push({
        date: dateStr,
        day: dateStr,
        dayOfWeek,
        sales: Math.round(sales * 100) / 100,
        customers: Math.round(customers * 100) / 100,
        hoursScheduled: hours.hoursScheduled,
        otHoursScheduled: hours.otHoursScheduled,
        metricId: existingMetricData?.id,
      });
    }

    return metrics;
  }, [startDate, existingMetrics, calculateHoursForDay]);

  const initialMetrics = useMemo(() => {
    return initializeMetrics();
  }, [initializeMetrics]);

  const { values, errors, touched, submitForm, handleChange, handleBlur } =
    useFormik<IEditStatsForm>({
      validationSchema,
      initialValues: {
        metrics: initialMetrics,
      },
      enableReinitialize: true,
      onSubmit: async (data: IEditStatsForm) => {
        try {
          for (const metric of data.metrics) {
            const dayHours = calculateHoursForDay(metric.day);
            const payload = buildSchedulerMetricPayload({
              workplaceId: workplaceId!,
              day: metric.day,
              sales: metric.sales,
              customers: metric.customers,
              hoursScheduled: dayHours.hoursScheduled,
              otHoursScheduled: dayHours.otHoursScheduled,
            });

            if (metric.metricId) {
              await updateMetricMutation.mutateAsync({ id: metric.metricId, payload });
            } else {
              await createMetricMutation.mutateAsync(payload);
            }
          }

          // Invalidate ALL scheduler metrics queries to ensure SchedulePage updates
          queryClient.invalidateQueries('scheduler-metrics');

          onBack();
        } catch (error) {
          showToast('error', 'Failed to save schedule statistics');
          console.error('Error saving metrics:', error);
        }
      },
    });

  const handleSubmitForm = (e: any) => {
    e.preventDefault();
    submitForm();
  }

  return (
    <Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
      <Box maxWidth="1100px" mx="auto" px={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Button
            onClick={onBack}
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 0.75,
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
          >
            Back
          </Button>

          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>Edit Schedule Budget</Box>

          <Button
            type="submit"
            variant="contained"
            form="editStatsForm"
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              px: 4,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
          >
            Save
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
            {isLoadingMetrics && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoadingMetrics && (

            <form id="editStatsForm" onSubmit={handleSubmitForm}>
              {values.metrics.map((metric, index) => (
                <Grid 
                  key={metric.day} 
                  container 
                  spacing={2} 
                  alignItems="center"
                  sx={{ 
                    mb: 2,
                    pb: 2,
                    borderBottom: index < values.metrics.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  {/* Day/Date Column */}
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                      {metric.dayOfWeek}
                    </Box>
                    <Box sx={{ fontSize: '0.85rem', color: '#666', mt: 0.5 }}>
                      {moment(metric.day).format('MMM D, YYYY')}
                    </Box>
                  </Grid>

                  {/* Sales Input */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name={`metrics.${index}.sales`}
                      type="number"
                      label="Projected Sales ($)"
                      size="small"
                      fullWidth
                      value={values.metrics[index].sales}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!(touched.metrics?.[index] &&
                          typeof errors.metrics?.[index] === 'object' &&
                          (errors.metrics?.[index] as any)?.sales)
                      }
                      helperText={
                        touched.metrics?.[index] &&
                        typeof errors.metrics?.[index] === 'object'
                          ? (errors.metrics?.[index] as any)?.sales
                          : ''
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  {/* Customers Input */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name={`metrics.${index}.customers`}
                      type="number"
                      label="Projected Customers"
                      size="small"
                      fullWidth
                      value={values.metrics[index].customers}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!(touched.metrics?.[index] &&
                          typeof errors.metrics?.[index] === 'object' &&
                          (errors.metrics?.[index] as any)?.customers)
                      }
                      helperText={
                        touched.metrics?.[index] &&
                        typeof errors.metrics?.[index] === 'object'
                          ? (errors.metrics?.[index] as any)?.customers
                          : ''
                      }
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                </Grid>
              ))}
            </form>
            )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EditStatsPage;
